import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Auth } from '@aws-amplify/auth';
import { GetDataEndpointCommand, KinesisVideoClient } from '@aws-sdk/client-kinesis-video';
import { ContainerFormat, GetHLSStreamingSessionURLCommand, HLSDiscontinuityMode, HLSDisplayFragmentTimestamp, HLSFragmentSelectorType, HLSPlaybackMode, KinesisVideoArchivedMediaClient, ListFragmentsCommand, } from '@aws-sdk/client-kinesis-video-archived-media';
import { Environment } from 'internal-config';
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
/* A bare bones video tab to display video from one hardcoded stream
 * The purpose of this is to provide a stable state to prove react/videojs plus hls on KVS
 *
 */
const { region } = Environment;
const StreamARN = `arn:aws:kinesisvideo:ap-southeast-2:493807289778:stream/v001-dev-IoTV_RAK_7248_V001_DEV-OPSEC_6/1787715068732`;
const ON_DEMAND_LOOKBACK_MS = 5 * 60 * 1000;
const STABLE_CLIP_DISCOVERY_START_UTC = '2026-09-10T17:00:00.000Z';
const STABLE_CLIP_DISCOVERY_START = new Date(STABLE_CLIP_DISCOVERY_START_UTC);
const FRAGMENT_GAP_THRESHOLD_MS = 20 * 1000;
const CLIP_END_PADDING_MS = 10 * 1000;
const MIN_CLIP_FRAGMENTS_FOR_AUTO_SELECT = 5;
const MIN_CLIP_FRAGMENTS_FOR_AUTO_ADVANCE = 2;
const MIN_CLIP_DURATION_MS_FOR_AUTO_ADVANCE = 3000;
const BUFFER_LOW_WATERMARK_SECONDS = 3;
const BUFFER_HIGH_WATERMARK_SECONDS = 10;
const MAX_MEDIA_PLAYLIST_FRAGMENT_RESULTS = 500;
const STALL_NUDGE_SECONDS = 0.25;
const BUFFER_APPEND_ERROR_PROFILE_SWITCH_THRESHOLD = 3;
const BUFFER_APPEND_ERROR_FAILOVER_THRESHOLD = 3;
const HLS_CONTAINER_FORMAT = ContainerFormat.FRAGMENTED_MP4;
// Returns seconds buffered ahead of the current playhead.
const getBufferedAheadSeconds = (video) => {
    const { buffered, currentTime } = video;
    for (let i = 0; i < buffered.length; i += 1) {
        const start = buffered.start(i);
        const end = buffered.end(i);
        if (currentTime >= start && currentTime <= end) {
            return Math.max(0, end - currentTime);
        }
    }
    return 0;
};
// Flips between producer and server timestamp selectors.
const getAlternateSelectorType = (selectorType) => {
    return selectorType === HLSFragmentSelectorType.PRODUCER_TIMESTAMP ? HLSFragmentSelectorType.SERVER_TIMESTAMP : HLSFragmentSelectorType.PRODUCER_TIMESTAMP;
};
// Extracts the timestamp used for ordering from a fragment.
const getFragmentTimestamp = (fragment, selectorType) => {
    const rawTimestamp = selectorType === HLSFragmentSelectorType.SERVER_TIMESTAMP ? fragment.ServerTimestamp : fragment.ProducerTimestamp;
    return rawTimestamp instanceof Date ? rawTimestamp : null;
};
// Groups fragments into contiguous clip windows for playback selection.
const buildClipWindows = (fragments, selectorType) => {
    const sortedFragments = fragments
        .map((fragment) => ({ fragment, timestamp: getFragmentTimestamp(fragment, selectorType) }))
        .filter((x) => x.timestamp instanceof Date)
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    if (sortedFragments.length === 0) {
        return [];
    }
    const windows = [];
    let windowStart = sortedFragments[0].timestamp;
    let windowEnd = sortedFragments[0].timestamp;
    let fragmentCount = 1;
    const pushWindow = () => {
        const paddedEnd = new Date(windowEnd.getTime() + CLIP_END_PADDING_MS);
        const id = `${selectorType}-${windowStart.getTime()}-${paddedEnd.getTime()}-${fragmentCount}`;
        windows.push({
            id,
            selectorType,
            startTimestamp: new Date(windowStart),
            endTimestamp: paddedEnd,
            fragmentCount,
            label: `${selectorType} ${windowStart.toLocaleString()} -> ${paddedEnd.toLocaleTimeString()} (${fragmentCount} frags)`,
        });
    };
    for (let i = 1; i < sortedFragments.length; i += 1) {
        const ts = sortedFragments[i].timestamp;
        if (ts.getTime() - windowEnd.getTime() <= FRAGMENT_GAP_THRESHOLD_MS) {
            windowEnd = ts;
            fragmentCount += 1;
        }
        else {
            pushWindow();
            windowStart = ts;
            windowEnd = ts;
            fragmentCount = 1;
        }
    }
    pushWindow();
    return windows.sort((a, b) => a.startTimestamp.getTime() - b.startTimestamp.getTime());
};
// Video lab tab that discovers historical windows and plays them through hls.js.
export const VideoLabTab = ({}) => {
    const [kvClient, setKvClient] = useState(null);
    const [kvaClient, setKvaClient] = useState(null);
    const [dataEndpoint, setDataEndpoint] = useState(null);
    const [hlsStreamingSessionURL, setHlsStreamingSessionURL] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [requestSummary, setRequestSummary] = useState('idle');
    const [lastPlayerEvent, setLastPlayerEvent] = useState('idle');
    const [nativeHlsSupport, setNativeHlsSupport] = useState('unknown');
    const [hlsRuntimeStatus, setHlsRuntimeStatus] = useState('idle');
    const [clipWindows, setClipWindows] = useState([]);
    const [selectedClipWindowId, setSelectedClipWindowId] = useState('');
    const [discoveryNonce, setDiscoveryNonce] = useState(0);
    const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [isPaused, setIsPaused] = useState(true);
    const [loadedFragmentCount, setLoadedFragmentCount] = useState(0);
    const [bufferedAheadSeconds, setBufferedAheadSeconds] = useState(0);
    const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);
    const [endedCount, setEndedCount] = useState(0);
    const [waitingFailoverCount, setWaitingFailoverCount] = useState(0);
    const [pendingAutoPlay, setPendingAutoPlay] = useState(false);
    const [stallRecoveryCount, setStallRecoveryCount] = useState(0);
    const [startupNudgeCount, setStartupNudgeCount] = useState(0);
    const [bufferAppendErrorCount, setBufferAppendErrorCount] = useState(0);
    const [badWindowIds, setBadWindowIds] = useState([]);
    const [manualResumeNeeded, setManualResumeNeeded] = useState(false);
    const [playbackProfile, setPlaybackProfile] = useState('A');
    const [profileSwitchCount, setProfileSwitchCount] = useState(0);
    const [selectorFallbackWindowIds, setSelectorFallbackWindowIds] = useState([]);
    const [selectorOverrideByWindowId, setSelectorOverrideByWindowId] = useState({});
    const [hlsErrorHistory, setHlsErrorHistory] = useState([]);
    const videoRef = useRef(null);
    const startupNudgeAtRef = useRef(0);
    const hlsJsSupported = Hls.isSupported();
    const hlsSupport = hlsJsSupported ? 'yes' : 'no';
    // Attempts to start playback and records whether user gesture is required.
    const tryStartPlayback = async (reason) => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        try {
            await video.play();
            setManualResumeNeeded(false);
            setErrorMessage(null);
            setRequestSummary((summary) => `${summary} (${reason}: playback started)`);
        }
        catch (error) {
            setManualResumeNeeded(true);
            setErrorMessage(`Playback start requires user gesture (${reason}): ${error?.message ?? 'Unknown error'}`);
        }
    };
    // Updates the last emitted player event for diagnostics.
    const updatePlayerEvent = (name) => {
        setLastPlayerEvent(name);
        console.log(`VideoLab player event: ${name}`);
    };
    // Determines whether a clip window is valid for automatic progression.
    const isAutoPlayableWindow = (window) => {
        const durationMs = window.endTimestamp.getTime() - window.startTimestamp.getTime();
        return window.fragmentCount >= MIN_CLIP_FRAGMENTS_FOR_AUTO_ADVANCE && durationMs >= MIN_CLIP_DURATION_MS_FOR_AUTO_ADVANCE;
    };
    // Advances to the next eligible clip window, wrapping around once.
    const advanceToNextClipWindow = (excludeWindowId) => {
        if (!autoAdvanceEnabled || clipWindows.length === 0) {
            return false;
        }
        const currentIndex = clipWindows.findIndex((window) => window.id === selectedClipWindowId);
        const orderedWindows = currentIndex >= 0 ? [...clipWindows.slice(currentIndex + 1), ...clipWindows.slice(0, currentIndex + 1)] : [...clipWindows];
        const excluded = new Set([...badWindowIds, ...(excludeWindowId ? [excludeWindowId] : [])]);
        const nextPlayableWindow = orderedWindows.find((window) => isAutoPlayableWindow(window) && !excluded.has(window.id));
        if (nextPlayableWindow) {
            setPlaybackProfile('A');
            setSelectedClipWindowId(nextPlayableWindow.id);
            setRequestSummary(`auto-advanced to ${nextPlayableWindow.label}`);
            setPendingAutoPlay(true);
            setManualResumeNeeded(false);
            return true;
        }
        setRequestSummary('auto-advance: no suitable next clip window found');
        return false;
    };
    // Handles clip completion and triggers auto-advance.
    const onEnded = () => {
        updatePlayerEvent('ended');
        setEndedCount((count) => count + 1);
        advanceToNextClipWindow();
    };
    // Handles explicit user click to resume or start playback.
    const onResumeClick = async () => {
        await tryStartPlayback('manual resume');
    };
    // Attempts autoplay after clip switches that set pendingAutoPlay.
    const onCanPlay = async () => {
        updatePlayerEvent('canplay');
        if (!pendingAutoPlay) {
            return;
        }
        const video = videoRef.current;
        if (!video) {
            setPendingAutoPlay(false);
            return;
        }
        try {
            await video.play();
            setRequestSummary((summary) => `${summary} (autoplay resumed)`);
            setManualResumeNeeded(false);
            setErrorMessage(null);
        }
        catch (error) {
            setRequestSummary((summary) => `${summary} (autoplay blocked)`);
            setErrorMessage(`Autoplay blocked after auto-advance: ${error?.message ?? 'Unknown error'}`);
            setManualResumeNeeded(true);
        }
        finally {
            setPendingAutoPlay(false);
        }
    };
    // Marks active playback and clears stale manual-resume state.
    const onPlaying = () => {
        updatePlayerEvent('playing');
        setManualResumeNeeded(false);
    };
    // Handles waiting states with short-clip failover and startup nudging.
    const onWaiting = () => {
        updatePlayerEvent('waiting');
        const video = videoRef.current;
        if (!video || !autoAdvanceEnabled) {
            return;
        }
        const nearStart = (video.currentTime || 0) < 0.5;
        const veryShortClip = Number.isFinite(video.duration) && video.duration > 0 && video.duration < 1;
        if (nearStart && veryShortClip) {
            setWaitingFailoverCount((count) => count + 1);
            setRequestSummary('waiting failover: advancing to next clip window');
            advanceToNextClipWindow();
            return;
        }
        const bufferedAhead = getBufferedAheadSeconds(video);
        const canNudge = Date.now() - startupNudgeAtRef.current > 1500;
        if (nearStart && bufferedAhead > 1 && canNudge) {
            let nudgeTarget = video.currentTime + STALL_NUDGE_SECONDS;
            if (video.buffered.length > 0) {
                const start = video.buffered.start(0);
                const end = video.buffered.end(0);
                nudgeTarget = Math.max(nudgeTarget, start + 0.05);
                nudgeTarget = Math.min(nudgeTarget, end - 0.05);
            }
            if (Number.isFinite(nudgeTarget) && nudgeTarget > video.currentTime) {
                const before = video.currentTime;
                video.currentTime = nudgeTarget;
                void video.play().catch(() => { });
                startupNudgeAtRef.current = Date.now();
                setStartupNudgeCount((count) => count + 1);
                setRequestSummary(`startup nudge ${before.toFixed(2)} -> ${nudgeTarget.toFixed(2)}s`);
            }
        }
    };
    useEffect(() => {
        // Initializes AWS KV client credentials for endpoint discovery.
        const initClient = async () => {
            try {
                const credentials = await Auth.currentUserCredentials();
                setKvClient(new KinesisVideoClient({ region, credentials }));
                setErrorMessage(null);
            }
            catch (error) {
                setErrorMessage(`Failed to initialize KinesisVideoClient: ${error?.message ?? 'Unknown error'}`);
            }
        };
        initClient();
    }, []);
    useEffect(() => {
        // Resolves the archived media endpoint and creates the archived media client.
        const fetchDataEndpoint = async () => {
            if (!kvClient)
                return;
            try {
                const { DataEndpoint } = await kvClient.send(new GetDataEndpointCommand({
                    StreamARN,
                    APIName: 'GET_HLS_STREAMING_SESSION_URL',
                }));
                const endpoint = DataEndpoint || null;
                setDataEndpoint(endpoint);
                if (!endpoint) {
                    setKvaClient(null);
                    setErrorMessage('No archived media endpoint returned from GetDataEndpoint');
                    return;
                }
                const credentials = await Auth.currentUserCredentials();
                setKvaClient(new KinesisVideoArchivedMediaClient({ region, credentials, endpoint }));
                setErrorMessage(null);
            }
            catch (error) {
                setErrorMessage(`Failed to get DataEndpoint: ${error?.message ?? 'Unknown error'}`);
            }
        };
        fetchDataEndpoint();
    }, [kvClient]);
    useEffect(() => {
        // Detects native HLS support capability for diagnostics.
        const video = videoRef.current;
        if (!video) {
            return;
        }
        const supportLevel = video.canPlayType('application/vnd.apple.mpegurl');
        setNativeHlsSupport(supportLevel || 'no');
    }, [hlsStreamingSessionURL]);
    useEffect(() => {
        // Discovers fragment-backed windows from the stable historical range.
        const discoverClipWindows = async () => {
            if (!kvaClient || !dataEndpoint)
                return;
            const selectorAttempts = [HLSFragmentSelectorType.PRODUCER_TIMESTAMP, HLSFragmentSelectorType.SERVER_TIMESTAMP];
            const endTimestamp = new Date();
            const startTimestamp = new Date(STABLE_CLIP_DISCOVERY_START);
            for (const selectorType of selectorAttempts) {
                try {
                    const { Fragments = [] } = await kvaClient.send(new ListFragmentsCommand({
                        StreamARN,
                        FragmentSelector: {
                            FragmentSelectorType: selectorType,
                            TimestampRange: {
                                StartTimestamp: startTimestamp,
                                EndTimestamp: endTimestamp,
                            },
                        },
                        MaxResults: 1000,
                    }));
                    const windows = buildClipWindows(Fragments, selectorType);
                    if (windows.length > 0) {
                        const autoSelectedWindow = [...windows].reverse().find((x) => x.fragmentCount >= MIN_CLIP_FRAGMENTS_FOR_AUTO_SELECT) ?? windows[windows.length - 1];
                        setClipWindows(windows);
                        setBadWindowIds([]);
                        setManualResumeNeeded(false);
                        setSelectorFallbackWindowIds([]);
                        setSelectorOverrideByWindowId({});
                        setPlaybackProfile('A');
                        setSelectedClipWindowId((prev) => (prev && windows.some((x) => x.id === prev) ? prev : autoSelectedWindow.id));
                        setRequestSummary(`found ${windows.length} clip windows via ${selectorType}`);
                        setErrorMessage(null);
                        return;
                    }
                }
                catch (error) {
                    setErrorMessage(`Error listing fragments for ${selectorType}: ${error?.message ?? 'Unknown error'}`);
                    return;
                }
            }
            setClipWindows([]);
            setSelectedClipWindowId('');
            setHlsStreamingSessionURL(null);
            setRequestSummary(`no clip windows found since ${STABLE_CLIP_DISCOVERY_START.toISOString()}`);
            setErrorMessage(`No fragments found since ${STABLE_CLIP_DISCOVERY_START.toISOString()} for producer/server timestamps`);
        };
        discoverClipWindows();
    }, [kvaClient, dataEndpoint, discoveryNonce]);
    useEffect(() => {
        // Requests an HLS session URL for the selected clip window.
        const fetchHLSStreamingSessionURL = async () => {
            if (!kvaClient || !dataEndpoint || !selectedClipWindowId)
                return;
            const selectedClipWindow = clipWindows.find((window) => window.id === selectedClipWindowId);
            if (!selectedClipWindow)
                return;
            const selectorType = selectorOverrideByWindowId[selectedClipWindow.id] ?? selectedClipWindow.selectorType;
            try {
                const { HLSStreamingSessionURL } = await kvaClient.send(new GetHLSStreamingSessionURLCommand({
                    StreamARN,
                    PlaybackMode: HLSPlaybackMode.ON_DEMAND,
                    ContainerFormat: HLS_CONTAINER_FORMAT,
                    DiscontinuityMode: HLSDiscontinuityMode.ALWAYS,
                    DisplayFragmentTimestamp: HLSDisplayFragmentTimestamp.ALWAYS,
                    MaxMediaPlaylistFragmentResults: MAX_MEDIA_PLAYLIST_FRAGMENT_RESULTS,
                    HLSFragmentSelector: {
                        FragmentSelectorType: selectorType,
                        TimestampRange: {
                            StartTimestamp: selectedClipWindow.startTimestamp,
                            EndTimestamp: selectedClipWindow.endTimestamp,
                        },
                    },
                }));
                setHlsStreamingSessionURL(HLSStreamingSessionURL || null);
                if (HLSStreamingSessionURL) {
                    setRequestSummary(`playing ${selectedClipWindow.label} [profile ${playbackProfile}] [selector ${selectorType}] [container ${HLS_CONTAINER_FORMAT}]`);
                    setErrorMessage(null);
                    setLoadedFragmentCount(0);
                    setStartupNudgeCount(0);
                    setBufferAppendErrorCount(0);
                    setHlsErrorHistory([]);
                    console.log(`HLS Streaming Session URL (${selectedClipWindow.label}): ${HLSStreamingSessionURL}`);
                }
                else {
                    setErrorMessage('No HLSStreamingSessionURL returned for selected clip window');
                }
            }
            catch (error) {
                setHlsStreamingSessionURL(null);
                setErrorMessage(`Error fetching HLSStreamingSessionURL: ${error?.message ?? 'Unknown error'}`);
            }
        };
        fetchHLSStreamingSessionURL();
    }, [kvaClient, dataEndpoint, clipWindows, selectedClipWindowId, playbackProfile, selectorOverrideByWindowId]);
    useEffect(() => {
        // Attaches hls.js to the video element and manages runtime recovery/telemetry.
        const video = videoRef.current;
        if (!video || !hlsStreamingSessionURL) {
            return;
        }
        if (!hlsJsSupported) {
            return;
        }
        const isProfileA = playbackProfile === 'A';
        const hls = new Hls({
            lowLatencyMode: false,
            startFragPrefetch: false,
            maxBufferLength: isProfileA ? BUFFER_HIGH_WATERMARK_SECONDS : 20,
            maxMaxBufferLength: isProfileA ? BUFFER_HIGH_WATERMARK_SECONDS : 20,
            backBufferLength: isProfileA ? 15 : 30,
            maxBufferHole: isProfileA ? 0.5 : 1.5,
            nudgeOffset: isProfileA ? 0.2 : 0.1,
            nudgeMaxRetry: isProfileA ? 8 : 3,
            highBufferWatchdogPeriod: isProfileA ? 1 : 2,
        });
        let isLoadStopped = false;
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            setHlsRuntimeStatus('media attached, loading source');
            hls.loadSource(hlsStreamingSessionURL);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
            setHlsRuntimeStatus(`manifest parsed, levels=${data.levels?.length ?? 0}`);
            updatePlayerEvent('manifest_parsed');
            void tryStartPlayback('manifest parsed');
        });
        hls.on(Hls.Events.FRAG_LOADED, () => {
            setLoadedFragmentCount((count) => count + 1);
        });
        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
            setHlsRuntimeStatus(`level switched to ${data.level}`);
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
            const details = `${data.type}:${data.details}`;
            setHlsRuntimeStatus(`hls error ${details}`);
            updatePlayerEvent(data.fatal ? `hls_fatal_${details}` : `hls_nonfatal_${details}`);
            setHlsErrorHistory((history) => {
                const stamp = new Date().toISOString();
                const next = [...history, `${stamp} ${data.fatal ? 'fatal' : 'nonfatal'} ${details}`];
                return next.slice(-10);
            });
            if (data.details === Hls.ErrorDetails.BUFFER_APPEND_ERROR) {
                setBufferAppendErrorCount((count) => {
                    const nextCount = count + 1;
                    if (playbackProfile === 'A' && nextCount >= BUFFER_APPEND_ERROR_PROFILE_SWITCH_THRESHOLD) {
                        setPlaybackProfile('B');
                        setProfileSwitchCount((switches) => switches + 1);
                        setRequestSummary(`bufferAppendError x${nextCount}: switching to profile B`);
                        return 0;
                    }
                    if (nextCount >= BUFFER_APPEND_ERROR_FAILOVER_THRESHOLD) {
                        const failingWindowId = selectedClipWindowId;
                        const failingWindow = clipWindows.find((window) => window.id === failingWindowId);
                        if (failingWindowId && failingWindow && !selectorFallbackWindowIds.includes(failingWindowId)) {
                            const alternateSelector = getAlternateSelectorType(selectorOverrideByWindowId[failingWindowId] ?? failingWindow.selectorType);
                            setSelectorOverrideByWindowId((overrides) => ({ ...overrides, [failingWindowId]: alternateSelector }));
                            setSelectorFallbackWindowIds((ids) => [...ids, failingWindowId]);
                            setPlaybackProfile('A');
                            setProfileSwitchCount((switches) => switches + 1);
                            setRequestSummary(`bufferAppendError x${nextCount}: retrying window with ${alternateSelector}`);
                            return 0;
                        }
                        if (failingWindowId) {
                            setBadWindowIds((ids) => (ids.includes(failingWindowId) ? ids : [...ids, failingWindowId]));
                        }
                        const advanced = advanceToNextClipWindow(failingWindowId);
                        if (!advanced) {
                            setRequestSummary(`bufferAppendError x${nextCount}: no alternate clip window found`);
                            setManualResumeNeeded(true);
                        }
                    }
                    return nextCount;
                });
            }
            if (!data.fatal && data.type === Hls.ErrorTypes.MEDIA_ERROR && data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
                const video = videoRef.current;
                if (video && Number.isFinite(video.currentTime)) {
                    const before = video.currentTime;
                    const after = Math.min(before + STALL_NUDGE_SECONDS, Number.isFinite(video.duration) ? video.duration : before + STALL_NUDGE_SECONDS);
                    video.currentTime = after;
                    void video.play().catch(() => { });
                    setStallRecoveryCount((count) => count + 1);
                    setHlsRuntimeStatus(`stall recovery: nudged ${before.toFixed(2)} -> ${after.toFixed(2)}s`);
                }
                hls.startLoad();
                isLoadStopped = false;
                return;
            }
            if (data.fatal) {
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        hls.startLoad();
                        isLoadStopped = false;
                        break;
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        hls.recoverMediaError();
                        break;
                    default:
                        hls.destroy();
                        break;
                }
            }
        });
        hls.attachMedia(video);
        const loadControlTimer = window.setInterval(() => {
            const bufferedAhead = getBufferedAheadSeconds(video);
            setBufferedAheadSeconds(bufferedAhead);
            if (playbackProfile === 'A' && !isLoadStopped && bufferedAhead >= BUFFER_HIGH_WATERMARK_SECONDS) {
                hls.stopLoad();
                isLoadStopped = true;
                setHlsRuntimeStatus(`load paused at buffer=${bufferedAhead.toFixed(2)}s`);
            }
            else if (playbackProfile === 'A' && isLoadStopped && bufferedAhead <= BUFFER_LOW_WATERMARK_SECONDS) {
                hls.startLoad();
                isLoadStopped = false;
                setHlsRuntimeStatus(`load resumed at buffer=${bufferedAhead.toFixed(2)}s`);
            }
        }, 500);
        return () => {
            window.clearInterval(loadControlTimer);
            hls.destroy();
        };
    }, [hlsStreamingSessionURL, hlsJsSupported, playbackProfile]);
    useEffect(() => {
        // Polls current media timing for lightweight UI diagnostics.
        const video = videoRef.current;
        if (!video) {
            return;
        }
        const timer = window.setInterval(() => {
            setCurrentTimeSeconds(video.currentTime || 0);
            setDurationSeconds(Number.isFinite(video.duration) ? video.duration : 0);
            setIsPaused(video.paused);
        }, 500);
        return () => {
            window.clearInterval(timer);
        };
    }, [hlsStreamingSessionURL]);
    const hlsRuntimeStatusDisplay = hlsJsSupported ? hlsRuntimeStatus : 'hls.js unsupported in this browser';
    const selectedClipWindow = clipWindows.find((window) => window.id === selectedClipWindowId) ?? null;
    return (_jsxs("div", { children: [_jsxs("div", { children: ["Stream ARN: ", StreamARN] }), _jsxs("div", { children: ["Data Endpoint: ", dataEndpoint ?? 'pending...'] }), _jsxs("div", { children: ["HLS URL: ", hlsStreamingSessionURL ?? 'pending...'] }), _jsxs("div", { children: ["Status: ", errorMessage ?? 'ready'] }), _jsxs("div", { children: ["Request Summary: ", requestSummary] }), _jsxs("div", { children: ["Discovered Clip Windows: ", clipWindows.length, _jsx("button", { style: { marginLeft: 8 }, onClick: () => setDiscoveryNonce((x) => x + 1), children: "Refresh Discovery" })] }), _jsxs("div", { children: ["Discovery Start (UTC): ", STABLE_CLIP_DISCOVERY_START.toISOString()] }), _jsx("div", { children: _jsxs("label", { children: ["Clip Window:", ' ', _jsx("select", { value: selectedClipWindowId, onChange: (e) => {
                                setSelectedClipWindowId(e.target.value);
                                setPlaybackProfile('A');
                                setManualResumeNeeded(false);
                                setRequestSummary('manual clip window selection');
                            }, disabled: clipWindows.length === 0, children: clipWindows.map((window) => (_jsx("option", { value: window.id, children: window.label }, window.id))) })] }) }), _jsxs("div", { children: ["Selected Window:", ' ', selectedClipWindow ? `${selectedClipWindow.startTimestamp.toISOString()} -> ${selectedClipWindow.endTimestamp.toISOString()}` : 'none'] }), _jsxs("div", { children: ["Selected Window Fragments: ", selectedClipWindow?.fragmentCount ?? 0] }), _jsxs("div", { children: ["Native HLS Support: ", nativeHlsSupport] }), _jsxs("div", { children: ["hls.js Support: ", hlsSupport] }), _jsxs("div", { children: ["hls.js Runtime: ", hlsRuntimeStatusDisplay] }), _jsxs("div", { children: ["Last Player Event: ", lastPlayerEvent] }), _jsxs("div", { children: ["Loaded Fragments (hls.js): ", loadedFragmentCount] }), _jsxs("div", { children: ["Buffered Ahead: ", bufferedAheadSeconds.toFixed(2), " seconds"] }), _jsxs("div", { children: ["Player Time: ", currentTimeSeconds.toFixed(2), " / ", durationSeconds.toFixed(2), " seconds"] }), _jsxs("div", { children: ["Player State: ", isPaused ? 'paused' : 'playing'] }), _jsxs("div", { children: ["Waiting Failovers: ", waitingFailoverCount] }), _jsxs("div", { children: ["Stall Recoveries: ", stallRecoveryCount] }), _jsxs("div", { children: ["Startup Nudges: ", startupNudgeCount] }), _jsxs("div", { children: ["Buffer Append Errors: ", bufferAppendErrorCount] }), _jsxs("div", { children: ["Playback Profile: ", playbackProfile] }), _jsxs("div", { children: ["Profile Switches: ", profileSwitchCount] }), _jsxs("div", { children: ["Selector Fallbacks Used: ", selectorFallbackWindowIds.length] }), _jsxs("div", { children: ["Bad Windows: ", badWindowIds.length] }), _jsxs("div", { children: ["Pending AutoPlay: ", pendingAutoPlay ? 'yes' : 'no'] }), _jsxs("div", { children: ["Manual Resume Needed: ", manualResumeNeeded ? 'yes' : 'no'] }), _jsxs("div", { children: ["Recent HLS Errors: ", hlsErrorHistory.length === 0 ? 'none' : hlsErrorHistory.join(' | ')] }), _jsx("div", { children: _jsx("button", { onClick: onResumeClick, children: "Start/Resume Playback" }) }), manualResumeNeeded ? (_jsx("div", { children: _jsx("button", { onClick: onResumeClick, children: "Resume Playback" }) })) : null, _jsx("div", { children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: autoAdvanceEnabled, onChange: (e) => setAutoAdvanceEnabled(e.target.checked) }), " Auto-advance on clip end"] }) }), _jsxs("div", { children: ["Clip End Events: ", endedCount] }), hlsStreamingSessionURL ? (_jsx("video", { ref: videoRef, controls: true, autoPlay: true, playsInline: true, preload: "metadata", src: Hls.isSupported() ? undefined : hlsStreamingSessionURL, style: { width: '100%', maxWidth: 960, marginTop: 12 }, onLoadedMetadata: () => updatePlayerEvent('loadedmetadata'), onCanPlay: onCanPlay, onPlaying: onPlaying, onWaiting: onWaiting, onStalled: () => updatePlayerEvent('stalled'), onEnded: onEnded, onError: () => updatePlayerEvent('error') })) : null] }));
};
